/**
 * Lógica pura de las cajas (Mati, 2026-10-19): dónde está la plata del fideicomiso. Opciones para elegir la caja de un
 * movimiento (aporte, cambio de moneda), nombre de una caja y el formulario de transferencia entre cajas. Mismos textos
 * de error que el backend (`app/caja/cajas.py`, `app/caja/transferencias.py`).
 */
import type { OpcionSelect } from "@/components";
import type { Caja, Moneda, Transferencia } from "./api";
import { hoyISO } from "./format";
import { montoAInput, parseMonto } from "./gastos";

export const LARGO_NOMBRE_CAJA = 60;

/** Las activas (la principal primero, como las manda el backend); una archivada sólo si es la que ya tenía el movimiento. */
export function opcionesCaja(cajas: Caja[], actual: number | null = null): OpcionSelect[] {
  return cajas
    .filter((c) => c.activo || c.id === actual)
    .map((c) => ({ valor: String(c.id), etiqueta: c.activo ? c.nombre : `${c.nombre} (archivada)` }));
}

/** El nombre de la caja de un movimiento para las listas (la archivada, marcada); "—" sin caja o si no se cargaron. */
export function nombreCaja(cajas: Caja[], id: number | null): string {
  const c = id === null ? undefined : cajas.find((x) => x.id === id);
  if (!c) return "—";
  return c.activo ? c.nombre : `${c.nombre} (archivada)`;
}

/** Con una sola caja activa no se pregunta: todo va a la principal. */
export function hayVariasCajas(cajas: Caja[]): boolean {
  return cajas.filter((c) => c.activo).length > 1;
}

export function cajaPorDefecto(cajas: Caja[]): string {
  const principal = cajas.find((c) => c.principal);
  return principal ? String(principal.id) : "";
}

export function validarNombreCaja(nombre: string): string | null {
  const limpio = nombre.trim();
  if (!limpio) return "Poné un nombre para la caja";
  if (limpio.length > LARGO_NOMBRE_CAJA) return `Hasta ${LARGO_NOMBRE_CAJA} caracteres`;
  return null;
}

export function mensajeBajaCaja(nombre: string, resultado: "borrado" | "archivado"): string {
  return resultado === "borrado" ? `Se borró la caja ${nombre}.` : `La caja ${nombre} tiene movimientos: quedó archivada.`;
}

// ------------------------------------------------------------------ transferencias entre cajas

export interface FormTransferencia {
  fecha: string;
  origen_id: string;
  destino_id: string;
  moneda: Moneda;
  monto: string;
  nota: string;
}

export type ErroresTransferencia = Partial<Record<keyof FormTransferencia, string>>;

export interface PayloadTransferencia {
  fecha: string;
  origen_id: number;
  destino_id: number;
  moneda: Moneda;
  monto: string;
  nota: string | null;
}

export function formTransferencia(t: Transferencia | undefined, cajas: Caja[], hoy = hoyISO()): FormTransferencia {
  if (!t) return { fecha: hoy, origen_id: cajaPorDefecto(cajas), destino_id: "", moneda: "ARS", monto: "", nota: "" };
  return { fecha: t.fecha, origen_id: String(t.origen_id), destino_id: String(t.destino_id), moneda: t.moneda, monto: montoAInput(t.monto), nota: t.nota ?? "" };
}

export function validarTransferencia(f: FormTransferencia): ErroresTransferencia {
  const e: ErroresTransferencia = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (!f.origen_id) e.origen_id = "Elegí de qué caja sale";
  if (!f.destino_id) e.destino_id = "Elegí a qué caja entra";
  else if (f.destino_id === f.origen_id) e.destino_id = "Elegí dos cajas distintas";
  if (parseMonto(f.monto) === null) e.monto = "El monto tiene que ser mayor que cero";
  if (f.nota.trim().length > 200) e.nota = "Hasta 200 caracteres";
  return e;
}

export function aPayloadTransferencia(f: FormTransferencia): PayloadTransferencia {
  return {
    fecha: f.fecha,
    origen_id: Number(f.origen_id),
    destino_id: Number(f.destino_id),
    moneda: f.moneda,
    monto: parseMonto(f.monto) ?? "0",
    nota: f.nota.trim() || null,
  };
}
